import { Link, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import {
  APP_NAME,
  APP_STORE_URL,
  ASSET,
  azaleaPath,
  SHARE_DESCRIPTION,
  SHARE_IMAGE,
  SHARE_TITLE,
  SITE_ORIGIN,
} from './constants';
import './azalea.css';

const PAGE_TITLES: Record<string, string> = {
  [azaleaPath()]: APP_NAME,
  [azaleaPath('privacy')]: `Privacy Policy, ${APP_NAME}`,
  [azaleaPath('terms')]: `Terms of Use, ${APP_NAME}`,
  [azaleaPath('faq')]: `FAQ, ${APP_NAME}`,
  [azaleaPath('support')]: `Support, ${APP_NAME}`,
  [azaleaPath('data-rights')]: `Data & Privacy Rights, ${APP_NAME}`,
};

const PAGE_DESCRIPTIONS: Record<string, string> = {
  [azaleaPath()]: SHARE_DESCRIPTION,
  [azaleaPath('privacy')]: 'Azalea Privacy Policy. No accounts, no cloud storage, no ads.',
  [azaleaPath('terms')]: 'Azalea Terms of Use. Communication support tool, not a medical device.',
  [azaleaPath('faq')]: 'Frequently asked questions about Azalea picture communication app.',
  [azaleaPath('support')]: 'Get help with Azalea. Contact support.',
  [azaleaPath('data-rights')]: 'Your data rights in Azalea. On device storage and deletion.',
};

function AppStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

export function AppStoreButton({ className = '' }: { className?: string }) {
  return (
    <a
      href={APP_STORE_URL}
      className={`azalea-appstore-btn ${className}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      <AppStoreIcon />
      Download on the App Store
    </a>
  );
}

export function AzaleaLayout() {
  const location = useLocation();
  const homePath = azaleaPath();
  const path = location.pathname.replace(/\/$/, '') || homePath.replace(/\/$/, '') || '/';

  useEffect(() => {
    document.title = PAGE_TITLES[path] ?? APP_NAME;

    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute('content') ?? '';

    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', PAGE_DESCRIPTIONS[path] ?? PAGE_DESCRIPTIONS[homePath]);

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', SHARE_TITLE);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute(
      'content',
      PAGE_DESCRIPTIONS[path] ?? PAGE_DESCRIPTIONS[homePath]
    );

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', SHARE_IMAGE);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', `${SITE_ORIGIN}${path === homePath ? '' : path}`);

    let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    const prevFavicon = favicon?.href ?? '';
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = ASSET('favicon.png');

    return () => {
      if (meta && prevDescription) meta.setAttribute('content', prevDescription);
      if (favicon && prevFavicon) favicon.href = prevFavicon;
    };
  }, [homePath, path]);

  return (
    <div className="azalea-page">
      <div className="azalea-header-bar">
        <header className="azalea-header">
          <Link to={azaleaPath()} className="azalea-logo">
            <img src={ASSET('azalea.png')} alt="" width={56} height={56} />
            {APP_NAME}
          </Link>
          <nav className="azalea-nav" aria-label="Azalea site navigation">
            <Link to={azaleaPath('faq')}>FAQ</Link>
            <Link to={azaleaPath('support')}>Support</Link>
            <Link to={azaleaPath('privacy')}>Privacy</Link>
            <Link to={azaleaPath('terms')}>Terms</Link>
          </nav>
        </header>
      </div>

      <Outlet />

      <footer className="azalea-footer">
        <div className="azalea-footer-inner">
          <Link to={azaleaPath()} className="azalea-footer-brand">
            <img src={ASSET('azalea.png')} alt="" width={48} height={48} />
            {APP_NAME}
          </Link>
          <div className="azalea-footer-links">
            <Link to={azaleaPath('privacy')}>Privacy</Link>
            <Link to={azaleaPath('terms')}>Terms</Link>
            <Link to={azaleaPath('faq')}>FAQ</Link>
            <Link to={azaleaPath('support')}>Support</Link>
            <Link to={azaleaPath('data-rights')}>Data Rights</Link>
          </div>
          <AppStoreButton />
          <p className="azalea-footer-meta">
            Made with care by{' '}
            <a href="https://humblestack.com" rel="noopener noreferrer" target="_blank">
              HumbleStack
            </a>
            <br />
            © {new Date().getFullYear()} {APP_NAME}
          </p>
        </div>
      </footer>
    </div>
  );
}
